import { useLayoutEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  keywords?: string;
  schema?: object | object[];
  noindex?: boolean;
  /**
   * When noindex=true, controls whether links are also nofollow.
   * Default true (noindex,nofollow) preserves prior behavior for admin/auth pages.
   * Pass false to emit `noindex, follow` — used for destination-only city pages
   * so link equity still flows to hub pages via internal links.
   */
  nofollow?: boolean;
  ogType?: string;
}

export const SEOHead = ({
  title,
  description,
  canonical,
  ogImage = 'https://www.lifetimemovers.us/images/lifetime-movers-hero-truck.jpg',
  keywords,
  schema,
  noindex = false,
  nofollow = true,
  ogType = 'website',
}: SEOHeadProps) => {
  // Use title directly - all data files now use standard format "LifeTime Movers | {Keyword}"
  // No normalization needed since source data is standardized
  
  // Immediate title update (runs during render, not after)
  if (typeof document !== 'undefined') {
    document.title = title;
  }

  useLayoutEffect(() => {
    // Update meta tags
    const updateMeta = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Standard meta
    updateMeta('description', description);
    if (keywords) updateMeta('keywords', keywords);
    updateMeta('robots', noindex ? `noindex, ${nofollow ? 'nofollow' : 'follow'}` : 'index, follow');

    // Open Graph
    updateMeta('og:title', title, true);
    updateMeta('og:description', description, true);
    updateMeta('og:url', canonical, true);
    updateMeta('og:image', ogImage, true);
    updateMeta('og:type', ogType, true);

    // Twitter (use property attribute to avoid duplicate detection with name="description")
    updateMeta('twitter:title', title, true);
    updateMeta('twitter:description', description, true);
    updateMeta('twitter:image', ogImage, true);

    // Canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.id = 'canonical-tag';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical);

    // CRITICAL: Always remove any existing dynamic schema before injecting new one
    // This prevents duplicate FAQPage errors when navigating between pages
    const existingDynamicSchema = document.getElementById('json-ld-schema');
    if (existingDynamicSchema) {
      existingDynamicSchema.remove();
    }

    // Schema types that are already defined statically in index.html
    // These IDs exist in index.html: static-organization-schema, static-localbusiness-schema, 
    // static-website-schema, static-breadcrumb-schema
    const STATIC_SCHEMA_TYPES = ['Organization', 'MovingCompany', 'WebSite'];
    
    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      
      // Filter out schemas that duplicate static schemas in index.html
      const filteredSchemas = schemas.filter(s => {
        const schemaType = (s as Record<string, unknown>)['@type'];
        // Keep page-specific schemas (FAQPage, BreadcrumbList, Service, etc.)
        // Filter out global ones that are static in index.html
        return !STATIC_SCHEMA_TYPES.includes(schemaType as string);
      });
      
      if (filteredSchemas.length > 0) {
        const schemaScript = document.createElement('script');
        schemaScript.id = 'json-ld-schema';
        schemaScript.type = 'application/ld+json';
        
        // Handle multiple schemas with @graph structure (Google-recommended)
        const outputSchema = filteredSchemas.length === 1 
          ? filteredSchemas[0]  // Single schema: output directly
          : {
              "@context": "https://schema.org",
              "@graph": filteredSchemas.map(s => {
                // Remove @context from individual schemas to avoid duplication
                const { "@context": _, ...rest } = s as Record<string, unknown>;
                return rest;
              })
            };
        
        schemaScript.textContent = JSON.stringify(outputSchema);
        document.head.appendChild(schemaScript);
      }
    }

    return () => {
      // Cleanup on unmount - remove dynamic schema
      const script = document.getElementById('json-ld-schema');
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [title, description, canonical, ogImage, keywords, schema, noindex, nofollow, ogType]);

  return null;
};
